import React, { useContext, useMemo } from "react";
import ExtraDetailsDrawer from "common/ExtraDetails/ExtraDetailsDrawer";
import { useRouteMatch } from "react-router-dom";
import {
  ADMIN_TRADING_CONFIGURATION_PATH,
  BOT_EXTRA_DETAILS_PATH,
  DRAWER_TRADES_TABLE_FULL_WIDTH,
  SMALL_SCREEN,
} from "common/constants";
import GeneralTab from "features/bots/BotDrawer/GeneralTab";
import DrawerTitleLayout from "common/Drawers/DrawerTitleLayout";
import { getBotExtraDetailsRoute } from "utils/routes";
import useTabsWithUrl from "common/Tabs/useTabsWithUrl";
import BotActions from "features/bots/common/BotActions";
import TextWithIcon from "common/Text/TextWithIcon";
import { ICON_TYPES } from "@shared/common/constants";
import { BotType, FilterType, LanguageNameSpaces } from "@shared/types";
import TradeDetailsProvider from "@shared/features/trades/TradeDetailsProvider";
import TimelinePage from "features/trades/TradeDrawer/Timeline/TimelinePage";
import { TradeDetailsContext } from "@shared/features/trades/TradeDetails.context";
import BotDetailsProvider from "@shared/features/bots/BotDetailsProvider";
import { BotDetailsContext } from "@shared/features/bots/BotDetails.context";
import TradeChart from "features/trades/TradeDrawer/TradeChart";
import useMedia from "utils/hooks/useMedia";
import GridsTab from "features/trades/TradeDrawer/Grids/GridsTab";
import { useDrawerDelayedUnmount } from "utils/hooks/misc";
import DrawerTradesPage from "features/trades/common/DrawerTradesPage";
import { openMessage } from "utils/misc";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { useTheme } from "styled-components";
import NewLookBotDrawerTitle from "features/bots/NewLook/NewLookBotDrawerTitle";

const BotDrawer = () => {
  const match = useRouteMatch(BOT_EXTRA_DETAILS_PATH);
  const { botId: botIdStr } = (match?.params || {}) as {
    botId?: string;
    tab: BotExtraDetailsTabs;
  };
  const adminConfigurationsMatch = useRouteMatch(
    ADMIN_TRADING_CONFIGURATION_PATH
  );

  const botId = botIdStr ? parseInt(botIdStr) : undefined;
  const { id: drawerBotId, isUnmounting } = useDrawerDelayedUnmount(botId);

  return (
    <TradeDetailsProvider
      botId={drawerBotId}
      isBotDrawer={true}
      isChannelConfiguration={!!adminConfigurationsMatch}
      openMessage={openMessage}
    >
      <BotDetailsProvider
        botId={drawerBotId}
        isChannelConfiguration={!!adminConfigurationsMatch}
      >
        <BotDrawerContent
          botId={drawerBotId}
          isOpen={!!botId && !isUnmounting}
        />
      </BotDetailsProvider>
    </TradeDetailsProvider>
  );
};

export default BotDrawer;

interface BotDrawerContentProps {
  botId?: number;
  isOpen?: boolean;
}

const BotDrawerContent = ({ botId, isOpen }: BotDrawerContentProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const {
    getTradeMetadataStatus,
    tradeMetadata,
    getTradeExtraInfoActivitiesStatus,
    getTradeExtraInfoOrdersStatus,
    getTradeExtraInfoGridsStatus,
  } = useContext(TradeDetailsContext)!;
  const { botData, botMetadata, getBotMetadataStatus, getBotDataStatus } =
    useContext(BotDetailsContext)!;
  const isSmallScreen = useMedia(
    [`(max-width: ${SMALL_SCREEN})`],
    [true],
    false
  );
  const { isNewLook } = useTheme();

  const tabToDisplayName = {
    [BotExtraDetailsTabs.GENERAL]: t("tabs.general"),
    [BotExtraDetailsTabs.TIMELINE]: t("tabs.timeline"),
    [BotExtraDetailsTabs.GRIDS]: t("tabs.grids"),
    [BotExtraDetailsTabs.TRADES]: t("tabs.trades"),
  };

  const activeTradeId = botMetadata?.active_trade_id;
  const botType = botMetadata?.bot_type;
  const isSignalsBot = botType === BotType.SIGNAL;
  const isHedgingBot = botType === BotType.HEDGING;
  const baseGetTradesArgs = useMemo(() => ({ dca_bot_id: botId }), [botId]);

  const showActiveTrades =
    botType &&
    [BotType.ASAP, BotType.GRID].includes(botType) &&
    !!activeTradeId;
  const isGridBot = botType === BotType.GRID;
  let defaultTab: BotExtraDetailsTabs | undefined;
  if (botMetadata) {
    defaultTab = showActiveTrades
      ? BotExtraDetailsTabs.TIMELINE
      : BotExtraDetailsTabs.GENERAL;
  }

  const { tabDisplayName, onChange: onTabChange } =
    useTabsWithUrl<BotExtraDetailsTabs>({
      tabToDisplayName,
      tabs: Object.values(BotExtraDetailsTabs),
      path: BOT_EXTRA_DETAILS_PATH,
      getPathFromTab: (tab, location) => {
        if (botId) {
          return getBotExtraDetailsRoute(
            location,
            botId?.toString() || "",
            tab
          );
        }
      },
      skipUrlInitialization: !isOpen,
      defaultTab,
    });

  const getBotTitle = () => {
    return (
      <TextWithIcon
        style={{ width: "100%" }}
        text={botMetadata?.name}
        iconType={isSignalsBot ? undefined : ICON_TYPES.COIN}
        coinOrExchangeName={isSignalsBot ? undefined : botData?.symbols?.[0]}
        iconLink={isSignalsBot ? botData?.sub_group_image : undefined}
        justify="center"
        iconStyle={{ borderRadius: 4, marginRight: 8 }}
      />
    );
  };

  const generalTab = botId ? (
    <GeneralTab botData={botData} botId={botId} />
  ) : null;

  const tabToComponent: {
    [key in BotExtraDetailsTabs]?: React.JSX.Element | null;
  } = showActiveTrades
    ? {
        [tabToDisplayName[BotExtraDetailsTabs.GENERAL]]: generalTab,
        [tabToDisplayName[BotExtraDetailsTabs.TIMELINE]]: (
          <TimelinePage style={{ paddingBottom: 50 }} />
        ),
      }
    : { [tabToDisplayName[BotExtraDetailsTabs.GENERAL]]: generalTab };
  if (showActiveTrades && isGridBot)
    tabToComponent[tabToDisplayName[BotExtraDetailsTabs.GRIDS]] = <GridsTab />;
  if (isSignalsBot || isHedgingBot) {
    tabToComponent[tabToDisplayName[BotExtraDetailsTabs.TRADES]] = (
      <DrawerTradesPage
        baseGetTradesArgs={baseGetTradesArgs}
        isDrawerOpen={!!isOpen}
        hideFilterTypes={TRADES_HIDE_FILTER_TYPES}
        tabKey={tabToDisplayName[BotExtraDetailsTabs.TRADES]}
      />
    );
  }

  const isLoadingDrawer =
    getBotMetadataStatus.isLoading ||
    getTradeMetadataStatus.isLoading ||
    !getBotMetadataStatus.currentData;

  const isTradesTab =
    tabDisplayName === tabToDisplayName[BotExtraDetailsTabs.TRADES];

  const commonTimelineQueries = [
    getTradeExtraInfoActivitiesStatus,
    getTradeExtraInfoOrdersStatus,
  ];
  const tabToQueries = {
    [tabToDisplayName[BotExtraDetailsTabs.GENERAL]]: [getBotDataStatus],
    [tabToDisplayName[BotExtraDetailsTabs.TIMELINE]]: commonTimelineQueries,
    [tabToDisplayName[BotExtraDetailsTabs.GRIDS]]: [
      ...commonTimelineQueries,
      getTradeExtraInfoGridsStatus,
    ],
  };

  return (
    <ExtraDetailsDrawer
      visible={!!isOpen}
      tabsToComponents={isLoadingDrawer ? {} : tabToComponent}
      tab={tabDisplayName}
      onTabChange={onTabChange}
      path={BOT_EXTRA_DETAILS_PATH}
      title={
        isNewLook ? (
          <NewLookBotDrawerTitle
            name={botMetadata?.name}
            botData={botData}
            actions={botData ? <BotActions bot={botData} /> : null}
          />
        ) : (
          <DrawerTitleLayout
            title={getBotTitle()}
            titleRowActions={botData ? <BotActions bot={botData} /> : null}
          />
        )
      }
      extension={
        showActiveTrades && tradeMetadata && !isSmallScreen ? (
          <TradeChart isExtension={true} />
        ) : undefined
      }
      isLoading={isLoadingDrawer}
      minWidth={isTradesTab ? DRAWER_TRADES_TABLE_FULL_WIDTH : undefined}
      tabToQueries={tabToQueries}
      flatListTabs={[tabToDisplayName[BotExtraDetailsTabs.TRADES]]}
    />
  );
};

const TRADES_HIDE_FILTER_TYPES = [
  FilterType.EXCHANGES,
  FilterType.CLIENTS,
  FilterType.SOURCE_TYPE,
  FilterType.SOURCE_NAME,
];

enum BotExtraDetailsTabs {
  GENERAL = "general",
  TIMELINE = "active-trade",
  GRIDS = "grids",
  TRADES = "trades",
}
